import { useState, useEffect } from "react";
import Button from "../../../../components/atomic/Button";
import { CollapsibleSidebar } from "src/components/atomic/CollapsibleSidebar";
import Timeline from "../Timeline";
import { EyeIcon } from "@heroicons/react/24/outline";
import { convertUTCtoIST } from "src/shared/utils/DateUtil";
import UploadedDocumentView from "../../UploadedDocumentView";
import { showAgencyName } from "./utils";
/* -------------------- Helpers -------------------- */

export function underscoreToCamelWords(str: string) {
  if (!str) return "";
  return str
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const getAddress = (metadataJson: any) => {
  if (metadataJson?.address) return metadataJson?.address;

  const { line1, line2, city, state, pincode } = metadataJson;
  return `${line1}, ${line2}, ${city}, ${state}, ${pincode}`;
};

/* -------------------- UI Components -------------------- */

function InfoRow({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex flex-col">
      <span className="text-[14px] font-semibold tracking-wide text-gray-500">
        {label}
      </span>
      <span className="text-[13px] text-gray-500 mt-[1px] leading-tight break-words">
        {value || "--"}
      </span>
    </div>
  );
}

export function AddressCard({ metadataJson }) {
  const address = getAddress(metadataJson);
  return (
    <>
      <h3 className="text-sm font-semibold tracking-wide text-gray-500 mb-6 relative inline-block">
        Address Details
        <span className="absolute left-0 -bottom-1 w-full h-[3px] bg-gradient-to-r from-indigo-400 to-indigo-600 rounded"></span>
      </h3>
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          <InfoRow label="Address" value={address} />
          <InfoRow
            label="Address Type"
            value={underscoreToCamelWords(metadataJson?.addressType)}
          />
        </div>
      </div>
    </>
  );
}
interface DocumentModalProps {
  open: boolean;
  onClose: () => void;
  docUrl: string | null;
  setObjectPath: Function;
  objectPath: string;
}

export const DocumentModal: React.FC<DocumentModalProps> = ({
  open,
  setObjectPath,
  objectPath
}) => {
  if (!open) return null;

  return (
    <UploadedDocumentView
      objectPath={objectPath}
      onClose={() => setObjectPath(null)}
    />
  );
};

export function FiDetailsCard({
  task,
  setDocUrl,
  setShowDocModal,
  handleTimelineClick,
  setObjectPath
}) {
  const showAgency = showAgencyName();
  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        <InfoRow label="Case ID" value={task.id} />
        <InfoRow
          label="Created At"
          value={convertUTCtoIST(task.createdAt, true)}
        />
        <InfoRow label="Created By" value={task.createdBy} />
        <InfoRow
          label="Current State"
          value={task.workflowInstance?.currentState}
        />
        {showAgency && (
          <InfoRow label="Agency Name" value={task.metadataJson?.agencyName} />
        )}
      </div>

      <div className="mt-4 border-t border-gray-200" />

      <div className="pt-2 items-center flex justify-between">
        <div className="flex items-center gap-2 pt-[1px] min-h-[20px]">
          {Array.isArray(task.taskAttachments) &&
            task.taskAttachments.length > 0 && (
              <div className="flex items-center gap-2 pt-[1px]">
                <h4 className="text-[14px] font-semibold tracking-wide text-gray-500">
                  Attachments
                </h4>
                <div className="flex gap-1">
                  {task.taskAttachments.map((doc: any) => (
                    <EyeIcon
                      className="w-5 h-5 cursor-pointer"
                      onClick={() => {
                        setDocUrl(doc.s3Url);
                        setShowDocModal(true);
                        setObjectPath(doc.s3Url);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
        </div>

        <Button
          outline
          color="primary"
          className="border-none p-2"
          onClick={() => handleTimelineClick(task.workflowInstance?.id)}
        >
          See Timeline
        </Button>
      </div>
    </div>
  );
}

export function FcuMetadata({ metadataJson }) {
  if (!metadataJson) return null;

  const showAgency = showAgencyName();

  // Case 1: fcuTrigger array → show Reason 1, Reason 2, ...
  if (Array.isArray(metadataJson?.fcuTrigger)) {
    return (
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        {showAgency && (
          <InfoRow label="Agency Name" value={metadataJson?.agencyName} />
        )}
        {metadataJson?.fcuTrigger.map((item, index) => (
          <InfoRow
            key={index}
            label={`Reason ${index + 1}`}
            value={item.reason}
          />
        ))}
      </div>
    );
  }

  // Case 2: Plain object → render key/value exactly like InfoRow
  return (
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      {Object.entries(metadataJson).map(([key, value]) => {
        if (key === "agencyName" && !showAgency) return;
        return (
          <InfoRow key={key} label={camelToTitle(key)} value={String(value)} />
        );
      })}
    </div>
  );
}

function camelToTitle(str = "") {
  return str.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

export function FcuDetailsCard({
  task,
  setDocUrl,
  setShowDocModal,
  handleTimelineClick,
  setObjectPath
}) {
  return (
    <div className="bg-white mt-4 transition-all duration-200">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        <InfoRow label="Case ID" value={task.id} />
        <InfoRow
          label="Created At"
          value={convertUTCtoIST(task.createdAt, true)}
        />
        <InfoRow label="Created By" value={task.createdBy} />
        <InfoRow
          label="Current State"
          value={task.workflowInstance?.currentState}
        />
      </div>

      <FcuMetadata metadataJson={task?.workflowInstance?.metadataJson} />

      <div className="mt-4 border-t border-gray-200" />

      <div className="pt-2 items-center flex justify-between">
        <div className="flex items-center gap-2 pt-[1px] min-h-[20px]">
          {Array.isArray(task.taskAttachments) &&
            task.taskAttachments.length > 0 && (
              <div className="flex items-center gap-2 pt-[1px]">
                <h4 className="text-[14px] font-semibold tracking-wide text-gray-500">
                  Attachments
                </h4>
                <div className="flex gap-1">
                  {task.taskAttachments.map((doc: any) => (
                    <EyeIcon
                      className="w-5 h-5 cursor-pointer"
                      onClick={() => {
                        setDocUrl(doc.s3Url);
                        setShowDocModal(true);
                        setObjectPath(doc.s3Url);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
        </div>

        <Button
          outline
          color="primary"
          className="border-none p-2"
          onClick={() => handleTimelineClick(task.workflowInstance?.id)}
        >
          See Timeline
        </Button>
      </div>
    </div>
  );
}

/* -------------------- Main Component -------------------- */

export default function DetailsComponent({
  graphqlResponse,
  token,
  whiteLabelStyle,
  cardIdName,
  activeTab
}: {
  graphqlResponse: any;
  token: string;
  whiteLabelStyle: any;
  cardIdName: string;
  activeTab: any;
}) {
  const [isTimelineOpen, setIsTimelineOpen] = useState<boolean>(false);
  const [workflowInstanceID, setWorkflowInstanceID] = useState<string>("");
  const [objectPath, setObjectPath] = useState<string | null>("");

  const sourceEntityID = graphqlResponse?.loanApplication?.sourceEntityID || "";
  const activeTabId = activeTab.id;

  const showAddressCard = activeTabId === "fi" || activeTabId === "fi_verify";
  const showFiDetailsCard = activeTabId === "fi" || activeTabId === "fi_verify";
  const showFcuDetailsCard = activeTabId === "fcu_verify";

  const taskList =
    activeTabId === "fi"
      ? graphqlResponse?.taskList?.filter((task: any) => {
          if (
            task?.workflowInstance?.metadataJson?.addressType === cardIdName
          ) {
            return task;
          }
        })
      : graphqlResponse?.taskList;

  console.log(activeTabId);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docUrl, setDocUrl] = useState("");

  const handleTimelineClick = (workflowInstanceID: string) => {
    setWorkflowInstanceID(workflowInstanceID);
    setIsTimelineOpen(true);
  };

  if (!Array.isArray(taskList) || taskList.length === 0) {
    return (
      <div className="text-gray-500 text-center py-6 text-sm">
        No tasks available.
      </div>
    );
  }

  return (
    <>
      <div className="p-3">
        {showAddressCard && (
          <AddressCard
            metadataJson={taskList[0]?.workflowInstance?.metadataJson}
          />
        )}
        {showFiDetailsCard && (
          <div className="space-y-5">
            <h3 className="text-sm font-semibold tracking-wide text-gray-500 mb-3 relative inline-block">
              Task Details
              <span className="absolute left-0 -bottom-1 w-full h-[3px] bg-gradient-to-r from-indigo-400 to-indigo-600 rounded"></span>
            </h3>
            {taskList?.map((task, idx) => (
              <FiDetailsCard
                key={task.id}
                task={task}
                setDocUrl={setDocUrl}
                setShowDocModal={setShowDocModal}
                handleTimelineClick={handleTimelineClick}
                setObjectPath={setObjectPath}
              />
            ))}
          </div>
        )}
        {showFcuDetailsCard && (
          <div className="space-y-5">
            <h3 className="text-sm font-semibold tracking-wide text-gray-500 mb-3 relative inline-block">
              FCU Details
              <span className="absolute left-0 -bottom-1 w-full h-[3px] bg-gradient-to-r from-indigo-400 to-indigo-600 rounded"></span>
            </h3>
            {taskList.map((task, idx) => (
              <FcuDetailsCard
                key={task.id}
                task={task}
                setDocUrl={setDocUrl}
                setShowDocModal={setShowDocModal}
                handleTimelineClick={handleTimelineClick}
                setObjectPath={setObjectPath}
              />
            ))}
          </div>
        )}
      </div>

      <CollapsibleSidebar
        show={isTimelineOpen}
        setShow={setIsTimelineOpen}
        customLayout
        title={"Task Timeline"}
        style={{
          container:
            "flex flex-col p-4 w-[35vw] min-w-[500px] h-[90vh] overflow-scroll"
        }}
      >
        <Timeline
          token={token}
          isOpen={isTimelineOpen}
          workflowInstanceID={workflowInstanceID}
          sourceEntityID={sourceEntityID}
          whiteLabelStyle={whiteLabelStyle}
        />
      </CollapsibleSidebar>

      <DocumentModal
        objectPath={objectPath}
        setObjectPath={setObjectPath}
        open={showDocModal}
        onClose={() => setShowDocModal(false)}
        docUrl={docUrl}
      />
    </>
  );
}
